// Use environment variable for API target
// For CRA: prefix must be REACT_APP_
// For Vite: prefix must be VITE_
const API_ROOT =
  process.env.REACT_APP_API_TARGET ||
  process.env.VITE_API_TARGET ||
  process.env.API_TARGET ||
  "https://api.wheresglutenfree.com/api";

const API_BASE = `${API_ROOT}/v1`;

/* ------------------ Category param helper ------------------ */
// Accepts: array (["Café / Coffee / Tea","Japanese"]) or string ("Cafe,Barbecue")
// Returns: "Café,Coffee,Tea,Japanese" (deduped, trimmed, order preserved)
function toCategoriesParam(categories) {
  if (!categories) return null;

  // normalize input to an array of tokens
  let arr = Array.isArray(categories) ? categories.slice() : String(categories).split(",");
  // split each token on '/', then trim
  arr = arr
    .flatMap((c) => String(c).split("/"))
    .map((s) => s.trim())
    .filter(Boolean);

  // de-dupe (case-insensitive) while preserving order
  const seen = new Set();
  const dedup = [];
  for (const t of arr) {
    const key = t.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      dedup.push(t);
    }
  }
  return dedup.join(",");
}


/* ------------------ Search + Auth helpers ------------------ */

export async function searchRestaurants({
  location,
  lat,
  lng,
  radius,
  orderby = "gf_safety_score",
  limit = 25,
  offset,
  cursor,
  signal,
  categories,
}) {
  const hasLocation = typeof location === "string" && location.trim().length > 0;
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  if (!hasLocation && !hasCoords) {
    throw new Error("Needs location or both lat and lng");
  }

  const params = new URLSearchParams();
  if (hasLocation) params.set("location", location.trim());
  if (hasCoords) {
    params.set("lat", String(lat));
    params.set("lng", String(lng));
  }

  params.set("allergen", "gluten");
  if (radius != null) params.set("radius", String(radius));
  if (orderby) params.set("orderby", orderby);
  if (limit != null) params.set("limit", String(limit));
  if (typeof offset === "number") {
    params.set("offset", String(Math.max(0, offset)));
  } else if (cursor) {
    params.set("cursor", cursor);
  }
  
  const catsCSV = toCategoriesParam(categories);
  if (catsCSV) params.set("categories", catsCSV);

  const url = `${API_BASE}/restaurants?${params.toString()}`;

  const res = await fetch(url, {
    headers: { Accept: "application/json" },
    signal,
    credentials: "include",
  });

  if (res.status === 429) {
    const reset = res.headers.get("RateLimit-Reset");
    const total = res.headers.get("RateLimit-Limit");
    const remaining = res.headers.get("RateLimit-Remaining");
    const err = new Error(`Rate limited. Try again in 60 seconds`);
    err.rate = { reset, total, remaining };
    throw err;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  return res.json();
}

/* ------------------ Generic JSON helper ------------------ */

async function jsonFetch(path, { method = "GET", body, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data?.error || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

/* ------------------ Auth/session ------------------ */

export function getSession() {
  return jsonFetch("/auth/session");
}
export function login({ email, password }) {
  return jsonFetch("/auth/login", { method: "POST", body: { email, password } });
}
export function register({ email, password, sensitivity, home_location }) {
  return jsonFetch("/auth/register", { method: "POST", body: { email, password, sensitivity, home_location } });
}
export function logout() {
  return jsonFetch("/auth/logout", { method: "POST" });
}

/* ------------------ Me (account) ------------------ */

export function getMe() {
  return jsonFetch("/me");
}
export function updateMe({ email, sensitivity, home_location }) {
  return jsonFetch("/me", { method: "PUT", body: { email, sensitivity, home_location } });
}
export function updatePassword({ current_password, new_password }) {
  return jsonFetch("/me/password", { method: "PUT", body: { current_password, new_password } });
}
export function updateSubscription() {
  return jsonFetch("/billing/checkout", { method: "POST", body: {} });
}
export function manageSubscription() {
  return jsonFetch("/billing/portal", { method: "POST", body: {} });
}

/* ------------------ Feedback ------------------ */

export function submitFeedback({ email, message, signal }) {
  // Reuses API_ROOT -> API_BASE -> jsonFetch
  return jsonFetch("/feedback", { method: "POST", body: { email, message }, signal });
}

/* ------------------ Analytics helpers ------------------ */

function uuidv4() {
  if (crypto?.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const CSID_KEY = "wgf_client_session_id";
export function getClientSessionId() {
  let id = localStorage.getItem(CSID_KEY);
  if (!id) {
    id = uuidv4();
    localStorage.setItem(CSID_KEY, id);
  }
  return id;
}

function sendBeaconJson(path, payload) {
  try {
    const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
    return navigator.sendBeacon?.(`${API_BASE}${path}`, blob);
  } catch {
    return false;
  }
}

export async function postAnalyticsImpressionsBatch({ client_session_id, search_id, plan_hint, results }) {
  const payload = { client_session_id, search_id, plan_hint, results };
  const beac = sendBeaconJson("/analytics/impressions", payload);
  if (beac) return { ok: true, via: "beacon" };
  return jsonFetch("/analytics/impressions", { method: "POST", body: payload });
}

export async function postAnalyticsClick({
  client_session_id,
  search_id,
  place_id,
  action,
  position,
  context,
}) {
  const body = {
    client_session_id,
    search_id,
    place_id,
    action,
    position,
    ...context,
    clicked_at: new Date().toISOString(),
  };
  const beac = sendBeaconJson("/analytics/click", body);
  if (beac) return { ok: true, via: "beacon" };
  return jsonFetch("/analytics/click", { method: "POST", body });
}
